/**
 * SchedulesTab Component
 * Tab content for selecting a schedule (routine, daily_schedule) in AddStepDialog.
 * Shows a searchable grid of schedules with a "New" button.
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Button,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  CalendarMonth as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Schedule, ScheduleType } from '../../../types/schedule';
import { CreateScheduleDialog } from '../CreateScheduleDialog';

interface SchedulesTabProps {
  schedules: Schedule[];
  deviceId: string;
  onSelect: (schedule: Schedule) => void;
  onScheduleCreated?: () => void;
  allowedTypes: ScheduleType[];
}

const TYPE_COLORS: Record<ScheduleType, string> = {
  routine: '#4caf50',
  daily_schedule: '#2196f3',
  first_then: '#ff9800',
  choice_board: '#9c27b0',
  waiting_board: '#607d8b',
  weekly_schedule: '#00bcd4',
};

export const SchedulesTab: React.FC<SchedulesTabProps> = ({
  schedules,
  deviceId,
  onSelect,
  onScheduleCreated,
  allowedTypes,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Filter schedules by search query
  const filteredSchedules = useMemo(() => {
    if (!searchQuery.trim()) return schedules;
    const query = searchQuery.toLowerCase();
    return schedules.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        (s.description?.toLowerCase().includes(query) ?? false)
    );
  }, [schedules, searchQuery]);

  const handleNewSchedule = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  const handleScheduleCreated = (scheduleId: string) => {
    setCreateDialogOpen(false);
    onScheduleCreated?.();
  };

  return (
    <Box>
      {/* New button and search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleNewSchedule}
        >
          {t('schedules.addStep.newSchedule')}
        </Button>
        <TextField
          fullWidth
          size="small"
          placeholder={t('common.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Empty state */}
      {schedules.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <ScheduleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography>
            {t('schedules.addStep.noSchedules')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewSchedule}
            sx={{ mt: 2 }}
          >
            {t('schedules.addStep.newSchedule')}
          </Button>
        </Box>
      )}

      {/* No matches */}
      {schedules.length > 0 && filteredSchedules.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {t('common.noResults')}
          </Typography>
        </Box>
      )}

      {/* Schedules grid */}
      {filteredSchedules.length > 0 && (
        <Grid container spacing={2}>
          {filteredSchedules.map((schedule) => {
            const icon = schedule.icon?.emoji || '📋';
            const typeColor = TYPE_COLORS[schedule.type] || '#757575';
            const stepCount = schedule.steps?.length ?? 0;

            return (
              <Grid size={{ xs: 12, sm: 6 }} key={schedule.id}>
                <Card
                  variant="outlined"
                  sx={{
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <CardActionArea onClick={() => onSelect(schedule)}>
                    <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box sx={{ fontSize: '1.75rem' }}>{icon}</Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 500 }}>
                          {schedule.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={t(`schedules.types.${schedule.type}`)}
                            size="small"
                            sx={{
                              bgcolor: typeColor,
                              color: 'white',
                              height: 20,
                              '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {t('schedules.stepsCount', { count: stepCount })}
                          </Typography>
                        </Box>
                        {schedule.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {schedule.description}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create Schedule Dialog */}
      <CreateScheduleDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        onCreated={handleScheduleCreated}
      />
    </Box>
  );
};
